import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Close from '@material-ui/icons/Close';
import VisibilityOffOutlined from '@material-ui/icons/VisibilityOffOutlined';
import {
  ArrowDropDown,
  ArrowDropUp,
  FilterListOutlined,
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { Row, Cell, Button, Notification, Tooltip } from '@workboard/wobo-ui';
import TeamMenu from '../components/menu/Menu';
import HoverMenu from '../components/hover-actions/HoverMenu';
import TeamTypeIcon from '../components/breadcrumbs/Icon';
import { FilterChip, IconType, TeamInterface } from '../types/teams.types';
import {
  CloseIconButton,
  InputWrapper,
  SearchIcon,
  SearchTextInput,
  StyledChip,
  SearchWrapper,
  RespSearchWrapper,
  CalDiv,
  TeamsNameLink,
  StyledRow,
  StyledTable,
  StyledCell,
  StyledSortable,
  Wrapper,
  FilterWrapper,
  FilterHeader,
  EmptyWrapper,
  StyledFilterChip,
  PopoverFooter,
  ActiveFiltersWrapper,
  FiltersWrapper,
  ChipsWrapper,
  BREAKPOINTS,
  ErrorMessage,
} from './styles/styles';
import EmptyTeamImage from '../../../vectors/emptyteam.svg';
import { StyledPopover } from './StyledPopover';
import CheckboxGroup from './checkbox/CheckboxGroup';
import Checkbox from './checkbox/Checkbox';
import {
  clearLocalActiveFilters,
  setLocalItemsSettings,
  getLocalItemsSettings,
  setLocalItemsPageSettings,
  getLocalItemsPageSettings,
} from './utils/storage';
import TeamServices from '../services/team.services';
import { WbEventHandlerMixins } from '../../../utils/common';
import { getInitialAdvanceArr, getInitialChipList } from '../types/teamsData';
import ArchieveModal from '../components/modal/ArchieveModal';
import { usePendoVisitorReport } from '../../../hooks/usePendoVisitorReport';

const teamServices = new TeamServices();

const TeamsTable = (): JSX.Element => {
  const { t } = useTranslation();
  const filterTypeArr = [t('State'), t('Team type'), t('Role')];
  const [teamsList, setTeamsList] = useState<TeamInterface[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [teamCount, setTeamCount] = useState({
    functionalTeam: 0,
    dynamicTeam: 0,
  });
  const [filteredItemsObj, setfilteredItemsObj] = useState({
    status: null,
    type: null,
    myRole: null,
  });
  const searchInputWrapperRef = useRef(null);
  const [isFocused, setFocused] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [chipList, setFilterTeams] = useState(getInitialChipList(t));
  const [alert, setAlert] = useState({
    show: false,
    type: 'success',
    message: '',
  });
  const userId = wbConfig.getUserId();
  const anchorEl = useState(null);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [openArchievePopup, setOpenArchievePopup] = useState<boolean>(false);
  const [arcId, setArcId] = useState(0);
  const [lastEventId, setLastEventId] = useState(null);
  const [arcActive, setArcActive] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [requestTeamEnabled, setRequestTeamEnabled] = useState(false);
  const [isPendoEnabled, setIsPendoEnabled] = useState(false);
  const [selectedFilterLength, setSelectedFilterLength] = useState(0);
  const [AdvanceFilterArr, setFilter] = useState(getInitialAdvanceArr(t));
  const [initialRender, setInitialRender] = useState(true);
  const [initialPageRender, setInitialPageRender] = useState(true);
  const [chipSelectedFilter, setChipFilterTeams] = useState('my');
  const currentSortOrder = getLocalItemsPageSettings().activeSortOrder;
  const currentColumnName = getLocalItemsPageSettings().activeColumn;
  const currentPerPage = getLocalItemsPageSettings().activePerPage;
  const [perPage, setPerPage] = useState(currentPerPage || 15);
  const [page, setPage] = useState(1);
  const [columnName, setColumnName] = useState(currentColumnName || '');
  const [order, setOrder] = useState(currentSortOrder || 'asc');
  const [archiveTeamType, setArchiveTeamType] = useState();

  usePendoVisitorReport(isPendoEnabled);

  const callTeams = useCallback(
    async (searchValue) => {
      try {
        const config = {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          params: {
            search: searchValue || null,
            chipFilter: chipSelectedFilter || 'my',
            status:
              filteredItemsObj.status && filteredItemsObj.status?.length > 0
                ? filteredItemsObj.status.toString()
                : undefined,
            type:
              filteredItemsObj.type && filteredItemsObj.type?.length > 0
                ? filteredItemsObj.type.toString()
                : undefined,
            myRole:
              filteredItemsObj.myRole && filteredItemsObj.myRole?.length > 0
                ? filteredItemsObj.myRole.toString()
                : undefined,
            perPage,
            page,
            column: columnName,
            order,
            userId,
          },
        };
        setIsLoading(true);
        const response = await axios.get('/wb/teamUserSearch/landing', config);
        if (response.data) {
          const teamMeta = response.data.meta;
          setTotalCount(response.data.pagination.total);
          setTeamCount({
            functionalTeam: teamMeta.quantity.functional,
            dynamicTeam: teamMeta.quantity.dynamic,
          });
          setRequestTeamEnabled(teamMeta.requestTeamEnabled);
          setIsPendoEnabled(teamMeta.isPendoEnabled);
          setTeamsList(response.data.data);
        } else {
          setAlert({
            show: true,
            type: 'error',
            message: t('Oops, something went wrong. Please try again later.'),
          });
        }
      } catch (e) {
        setAlert({
          show: true,
          type: 'error',
          message: t('Oops, something went wrong. Please try again later.'),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [
      perPage,
      page,
      columnName,
      order,
      userId,
      chipSelectedFilter,
      filteredItemsObj,
    ],
  );

  useEffect(() => {
    const pinUnpinFav = new BroadcastChannel('vnext: pinUnpinFav');
    pinUnpinFav.onmessage = (e) => {
      const pinnedItem = JSON.parse(e.data);
      setTeamsList(
        teamsList.map((team) => {
          if (team.id === Number(pinnedItem.obj_id)) {
            team.isFavorite = pinnedItem.pinnedStatus;
          }
          return team;
        }),
      );
    };
    return () => {
      pinUnpinFav.close();
    };
  }, [teamsList]);

  const clearFilter = (resetBtn): void => {
    const resetfilter = getInitialAdvanceArr(t).map((ele) => {
      ele.isSelected = false;
      return ele;
    });
    if (!resetBtn) {
      setLastEventId(document.querySelectorAll('[aria-label="Close"]')[1]);
    } else {
      setLastEventId(document.getElementById('filterButtonChip'));
    }
    setFilter(resetfilter);
    setSelectedFilterLength(0);
    const data = {
      status: null,
      type: null,
      myRole: null,
    };
    setfilteredItemsObj(data);
    clearLocalActiveFilters();
  };

  const actions = (): JSX.Element => (
    <PopoverFooter>
      <Button
        variant="ghost"
        onClick={clearFilter}
        isDisabled={
          filteredItemsObj.status === null &&
          filteredItemsObj.myRole === null &&
          filteredItemsObj.type === null
        }
      >
        {t('Reset filters')}
      </Button>
    </PopoverFooter>
  );

  const handleTableChange = (pageData: {
    page: number;
    rowsPerPage: number;
    columnName: string;
    order: string;
  }): void => {
    if (pageData.page + 1 !== page && pageData.page >= 0) {
      setPage(pageData.page + 1);
    }
    if (pageData.rowsPerPage !== perPage) {
      setPerPage(pageData.rowsPerPage);
    }
    if (pageData.columnName !== columnName) {
      setColumnName(pageData.columnName);
    }
    if (pageData.order !== order) {
      setOrder(pageData.order);
    }
    setLocalItemsPageSettings({
      activeSortOrder: order,
      activeColumn: columnName,
      activePerPage: perPage,
    });
  };

  const handleNotificationClose = (): void =>
    setAlert({ show: false, type: 'success', message: '' });

  const maxWidthIs600 = useMediaQuery(BREAKPOINTS.xs);
  const maxWidthIs900 = useMediaQuery(BREAKPOINTS.md);
  const maxWidthIs720 = useMediaQuery(BREAKPOINTS.sm);
  const maxWidthIs980 = useMediaQuery(BREAKPOINTS.lg);
  const maxWidthIs1100 = useMediaQuery(BREAKPOINTS.xl);

  const filterTeams = (item: FilterChip): void => {
    setFilterTeams((list) =>
      list.map((obj) => {
        if (obj.id === item.id) {
          const temp = { ...obj, isActive: true };
          if (temp.isActive) {
            setChipFilterTeams(temp.filterRef);
            setLocalItemsSettings({
              activeChipFilter: temp.filterRef,
              activeFilters: filteredItemsObj,
            });
          }
          return temp;
        }
        return { ...obj, isActive: false };
      }),
    );
  };

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLElement>,
    item,
  ): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      filterTeams(item);
    }
  };

  const teamsPageRedirect = (id: number): void => {
    // @ts-ignore
    ajaxPageLoad(
      `${wbConfig.baseUrl}/activity/workstreams?teamId=${id}&teamView`,
      undefined,
      undefined,
      undefined,
      undefined,
    );
  };

  const teamsRedirect = (id: number): void => {
    axios({
      method: 'get',
      url: `${window.wbbaseUrl}/teams/request`,
      params: {
        teamId: id,
        requestorId: userId,
      },
    }).then((res) => {
      if (res.data.message.type !== 'error') {
        if (res.data.data.length) {
          toastr.error(
            t('Your request to join this team is currently pending'),
          );
        } else {
          teamsPageRedirect(id);
          setTimeout(() => {
            document.getElementById('requestToJoinBtn').click();
          }, 1000);
        }
      } else {
        setAlert({
          show: true,
          type: 'error',
          message: t('Oops, something went wrong. Please try again later.'),
        });
      }
    });
  };

  useEffect(() => {
    const initialFilterObj = { status: null, type: null, myRole: null };
    const { activeSortOrder } = getLocalItemsPageSettings();
    const { activeChipFilter: activeStoredChip, activeFilters } =
      getLocalItemsSettings();
    const activeStoredFiltersObj: any = activeFilters;
    let selectedFilterList;
    if (activeStoredChip || activeStoredFiltersObj || activeSortOrder) {
      if (activeSortOrder) {
        setOrder(order);
        setPerPage(perPage);
        setColumnName(columnName);
      }
      if (activeStoredChip) {
        setChipFilterTeams(activeStoredChip);
        const tempList = getInitialChipList(t).map((item) => {
          if (item.filterRef == activeStoredChip) {
            return { ...item, isActive: true };
          }
          return { ...item, isActive: false };
        });
        setFilterTeams(tempList);
      }
      if (activeStoredFiltersObj) {
        selectedFilterList = getInitialAdvanceArr(t).map((item) => {
          if (
            activeStoredFiltersObj[item.filterKey] &&
            activeStoredFiltersObj[item.filterKey].includes(
              item.filterRef.toString(),
            )
          ) {
            item.isSelected = true;
          }
          return item;
        });
      } else {
        selectedFilterList = getInitialAdvanceArr(t);
      }
      setfilteredItemsObj(activeStoredFiltersObj || initialFilterObj);
      setFilter(selectedFilterList);
    } else {
      setChipFilterTeams('my');
      setfilteredItemsObj(initialFilterObj);
    }
  }, []);

  useEffect(() => {
    if (initialPageRender) {
      setInitialPageRender(false);
    } else {
      setLocalItemsPageSettings({
        activeSortOrder: order,
        activeColumn: columnName,
        activePerPage: perPage,
      });
      callTeams(searchText);
    }
  }, [page, perPage, columnName, order, chipSelectedFilter, filteredItemsObj]);

  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [chipSelectedFilter]);

  useEffect(() => {
    if (initialRender) {
      setInitialRender(false);
    } else {
      const delayDebounceFn = setTimeout(() => {
        callTeams(searchText);
      }, 300);
      return () => clearTimeout(delayDebounceFn);
    }
    return undefined;
  }, [searchText]);

  useEffect(() => {
    if (!popoverVisible) {
      window.setTimeout(() => {
        document.getElementById('filterButtonChip')?.focus();
      }, 0);
    }
  }, [popoverVisible]);

  useEffect(() => {
    if (AdvanceFilterArr) {
      let length = 0;
      AdvanceFilterArr.map((item) => {
        if (item.isSelected) {
          length++;
        }
        return item;
      });
      setSelectedFilterLength(length);
    }
  }, [AdvanceFilterArr]);

  const setFocusForSelectedFilterChips = (item) => {
    const elms = document.querySelectorAll('[id ^= "selected-filter"]');
    const totalLength = elms.length;
    elms.forEach((ele, index) => {
      let nextFocusableEle = '';
      if (ele.id === `selected-filter-${item.id}`) {
        if (index === totalLength - 1) {
          nextFocusableEle = elms[0].id;
        } else {
          nextFocusableEle = elms[index + 1].id;
        }
        setLastEventId(document.getElementById(nextFocusableEle).children[1]);
      }
      if (index === 0 && selectedFilterLength === 1) {
        setLastEventId(document.getElementById('filterButtonChip'));
      }
    });
  };
  const onToggleBox = (e, item, filterCancelBtn?): void => {
    let temp;
    const selectedFilterObj: any = { status: [], type: [], myRole: [] };
    setFilter((list) =>
      list.map((ele) => {
        if (ele.id === item.id) {
          temp = { ...ele, isSelected: !ele.isSelected };
        } else {
          temp = { ...ele, isSelected: ele.isSelected };
        }
        if (temp.isSelected) {
          selectedFilterObj[ele.filterKey].push(ele.filterRef);
        }
        if (selectedFilterObj[ele.filterKey]?.length === 0) {
          selectedFilterObj[ele.filterKey] = [];
        }
        setfilteredItemsObj(selectedFilterObj);
        return temp;
      }),
    );
    setLocalItemsSettings({
      activeChipFilter: chipSelectedFilter,
      activeFilters: selectedFilterObj,
    });
    if (filterCancelBtn) {
      setFocusForSelectedFilterChips(item);
    }
  };

  const handleFocus = (): void => {
    setFocused(true);
    window.setTimeout(() => {
      document.getElementById('teamsSearchInput')?.focus();
    }, 0);
  };
  const clearField = (): void => {
    document.getElementById('teamsSearchInput')?.focus();
    setSearchText('');
  };

  useEffect(() => {
    if (!isLoading) {
      window.setTimeout(() => {
        lastEventId?.focus();
        setLastEventId(null);
      }, 0);
    }
  }, [isLoading]);

  const text1100Style = maxWidthIs1100
    ? { maxWidth: 'calc(100vw - 720px)' }
    : { maxWidth: 'calc(100vw - 840px)' };

  const text980Style = maxWidthIs980
    ? { maxWidth: 'calc(100vw - 640px)' }
    : text1100Style;

  const text900Style = maxWidthIs900
    ? { maxWidth: 'calc(100vw - 530px)' }
    : text980Style;

  const text720Style = maxWidthIs720
    ? { maxWidth: 'calc(100vw - 440px)' }
    : text900Style;

  const renderSearchWrapper = (): JSX.Element => (
    <InputWrapper ref={searchInputWrapperRef}>
      <SearchIcon name="Search" color="rgb(196, 203, 215)" />
      <SearchTextInput
        id="teamsSearchInput"
        type="text"
        autoComplete="off"
        placeholder={t('Search for a manager or team')}
        autoFocus={isFocused}
        value={searchText}
        onChange={(e) => {
          setSearchText(e.target.value);
        }}
        onFocus={() => handleFocus()}
      />
      <CloseIconButton
        id="teamsSearchInputClear"
        compact
        style={searchText !== '' ? {} : { display: 'none' }}
        onClick={() => clearField()}
      >
        <Close />
      </CloseIconButton>
    </InputWrapper>
  );

  useEffect(() => {
    if (arcId > 0) setOpenArchievePopup(true);
  }, [arcActive]);

  const closeArchieveDialog = (): void => {
    setArcId(0);
    setArcActive(null);
    setOpenArchievePopup(false);
  };

  const TableRow = ({
    id,
    name,
    managerName,
    memberCount,
    myRole,
    type,
    isFavorite,
    isVisible,
    isActive,
  }): JSX.Element => {
    // @ts-ignore
    const url = `${window.location.origin}${wbConfig.baseUrl}/activity/workstreams?teamId=${id}&teamView`;
    const [isFav, setFav] = useState(isFavorite);

    const Archieveteam = (): void => {
      setArcId(id);
      setArcActive(isActive ? 0 : 1);
      setArchiveTeamType(type);
    };

    const FavoriteTeam = (): void => {
      const params = { obj_id: id, obj_type: 'team', user_id: userId };
      const message = isFav
        ? t('Team has been removed from your favorites.')
        : t('Team has been added to your favorites.');
      teamServices
        .userFavorites(isFav, params)
        .then((resp) => {
          if (resp.data.status == 'success') {
            toastr.success(message);
            setFav(isFav ? 0 : 1);
            WbEventHandlerMixins.pinGoal(
              params.obj_type,
              resp.data,
              0,
              params.obj_id,
            );
            if (chipSelectedFilter === 'favorite') {
              callTeams(searchText);
            }
          } else {
            toastr.error(
              t('Oops, something went wrong. Please try again later.'),
            );
          }
        })
        .catch(() => {
          toastr.error(
            t('Oops, something went wrong. Please try again later.'),
          );
        });
    };

    return (
      <StyledRow>
        <StyledCell
          style={{
            verticalAlign: 'middle',
            textAlign: 'center',
            lineHeight: 'normal',
          }}
        >
          <Tooltip
            arrow
            compact
            isTitle={false}
            placement="top"
            content={
              type !== 2 ? (
                <span>
                  <b>{t('Functional team')}</b> -{' '}
                  {t(
                    'The core teams that reflect your organizational functional hierarchy.',
                  )}
                </span>
              ) : (
                <span>
                  <b>{t('Dynamic team')}</b> -{' '}
                  {t(
                    'The cross-functional or dynamic teams to organize and coordinate initiatives as needed.',
                  )}
                </span>
              )
            }
          >
            <span>
              <TeamTypeIcon
                type={type !== 2 ? IconType.Functional : IconType.Dynamic}
                blueColor
              />
            </span>
          </Tooltip>
        </StyledCell>
        <StyledCell>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Tooltip
              arrow
              compact
              isTitle={false}
              placement="top"
              content={<span>{name}</span>}
            >
              <TeamsNameLink
                as="a"
                href={url}
                style={
                  maxWidthIs600
                    ? {
                        maxWidth: 'calc(100vw - 145px)',
                      }
                    : text720Style
                }
                onClick={(e) => {
                  e.preventDefault();
                  teamsPageRedirect(id);
                }}
                size="small"
              >
                {name}
              </TeamsNameLink>
            </Tooltip>
            {!isVisible && (
              <Tooltip
                arrow
                compact
                isTitle={false}
                placement="top"
                content={
                  <span>
                    <b>{t('Hidden team')}</b> - this team is only visible to its
                    members, and it is not discoverable in search results or
                    team lists to non-members.
                  </span>
                }
              >
                <VisibilityOffOutlined
                  style={{
                    color: '#687587',
                    width: '16px',
                    height: '16px',
                  }}
                />
              </Tooltip>
            )}
          </div>
        </StyledCell>
        {!maxWidthIs600 && (
          <StyledCell
            style={!maxWidthIs720 ? { minWidth: 184 } : { minWidth: 120 }}
          >
            {managerName}
          </StyledCell>
        )}
        {!maxWidthIs600 && (
          <StyledCell>{myRole != null ? myRole : '---'}</StyledCell>
        )}
        {!maxWidthIs900 && <StyledCell>{memberCount}</StyledCell>}
        {!maxWidthIs1100 && (
          <StyledCell>
            <HoverMenu
              id={id}
              userId={userId}
              url={url}
              isFavorite={isFav}
              onClickPin={FavoriteTeam}
            />
          </StyledCell>
        )}
        <Cell style={{ padding: '8px 0px', maxWidth: 48 }}>
          <TeamMenu
            id={id}
            userId={userId}
            url={url}
            isFavorite={isFav}
            role={myRole}
            isActive={isActive}
            requestEnabled={requestTeamEnabled}
            onClickPin={FavoriteTeam}
            onClickArchieve={Archieveteam}
            onClickRequest={teamsRedirect}
            type={type}
          />
        </Cell>
      </StyledRow>
    );
  };

  const TableHeader = (props): JSX.Element => (
    <Row>
      <StyledSortable
        {...props}
        className="columnType"
        style={{ width: 48, padding: '8px 10px', fontSize: 0 }}
      >
        {t('Type')}
      </StyledSortable>
      <StyledSortable {...props} style={{ width: '100%' }}>
        {t('Name')}
      </StyledSortable>
      {!maxWidthIs600 && (
        <StyledSortable
          {...props}
          style={!maxWidthIs720 ? { minWidth: 184 } : { minWidth: 120 }}
        >
          {t('Manager')}
        </StyledSortable>
      )}
      {!maxWidthIs600 && (
        <StyledSortable {...props} style={{ minWidth: 136 }}>
          {t('My Role')}
        </StyledSortable>
      )}
      {!maxWidthIs900 && (
        <StyledSortable {...props} style={{ maxWidth: 96 }}>
          {t('Members')}
        </StyledSortable>
      )}
      {!maxWidthIs1100 && (
        <Cell
          style={{ background: '#fff', maxWidth: 112, padding: '8px 13.5px' }}
        >
          {' '}
        </Cell>
      )}
      <Cell style={{ background: '#fff', maxWidth: 48, padding: '8px 3px' }}>
        {t('Actions')}
      </Cell>
    </Row>
  );

  return (
    <>
      <div>
        {maxWidthIs1100 && (
          <RespSearchWrapper>{renderSearchWrapper()}</RespSearchWrapper>
        )}
        <FilterWrapper>
          <Wrapper>
            {chipList.map((item: FilterChip) => (
              <StyledChip
                $active={item.isActive}
                disabled={isLoading}
                key={item.id}
                color="primary"
                icon={item.icon}
                id={item.id}
                rootProps={{
                  'aria-pressed': item.isActive,
                }}
                label={item.name}
                onClick={() =>
                  !isLoading && !item.isActive ? filterTeams(item) : null
                }
                type={item.isActive ? 'filtered' : 'filter'}
                {...(!item.isActive && {
                  onKeyDown: (event: React.KeyboardEvent<HTMLElement>) =>
                    handleKeyPress(event, item),
                })}
              />
            ))}
          </Wrapper>
          {!maxWidthIs1100 && (
            <SearchWrapper>{renderSearchWrapper()}</SearchWrapper>
          )}
          <div
            role="button"
            tabIndex={0}
            onClick={() => setPopoverVisible((visible) => !visible)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setPopoverVisible((visible) => !visible);
              }
            }}
          >
            <StyledFilterChip
              $selected={popoverVisible || selectedFilterLength > 0}
              ref={anchorEl}
              id="filterButtonChip"
              label={
                <div>
                  {t('Filters')}
                  {selectedFilterLength ? ` (${selectedFilterLength})` : ''}
                </div>
              }
              type={
                popoverVisible || selectedFilterLength > 0
                  ? 'filtered'
                  : 'filter'
              }
              icon={<FilterListOutlined />}
              onButtonClick={() => undefined}
              buttonIcon={popoverVisible ? <ArrowDropUp /> : <ArrowDropDown />}
            />

            <StyledPopover
              open={popoverVisible}
              title={
                <FilterHeader>
                  {t('Filters')}
                  {selectedFilterLength ? ` (${selectedFilterLength})` : ''}
                </FilterHeader>
              }
              targetRef={anchorEl}
              placement="bottom-end"
              arrow={false}
              actions={actions}
              popperOptions={{ strategy: 'absolute' }}
              removeActions={false}
              removeClose={false}
              onDismiss={() => setPopoverVisible(false)}
              content={
                <div>
                  {filterTypeArr.map((item) => (
                    <CheckboxGroup
                      key={item}
                      data-testid="filter-group-state"
                      label={item}
                    >
                      {AdvanceFilterArr.map(
                        (filterKey) =>
                          filterKey.type === item && (
                            <Checkbox
                              key={filterKey.id}
                              data-filterkey={filterKey}
                              data-sectionkey={item}
                              // @ts-ignore
                              label={filterKey.label}
                              isDisabled={isLoading}
                              isChecked={filterKey.isSelected}
                              onChange={(e) => {
                                if (!isLoading) {
                                  onToggleBox(e, filterKey);
                                }
                              }}
                            />
                          ),
                      )}
                    </CheckboxGroup>
                  ))}
                </div>
              }
            />
          </div>
        </FilterWrapper>

        <CalDiv>
          {(teamCount.functionalTeam > 0
            ? `${teamCount.functionalTeam} ${t('Functional teams')}`
            : ``) +
            (teamCount.dynamicTeam > 0 && teamCount.functionalTeam > 0
              ? `, `
              : ``) +
            (teamCount.dynamicTeam > 0
              ? `${teamCount.dynamicTeam} ${t('Dynamic teams')}`
              : ``)}
          <ActiveFiltersWrapper>
            {selectedFilterLength > 0 && (
              <FiltersWrapper
                $display={
                  teamCount.dynamicTeam > 0 || teamCount.functionalTeam > 0
                }
              >
                <span>{t('Filtered by:')}</span>
                <ChipsWrapper>
                  {AdvanceFilterArr.map(
                    (filterKey) =>
                      filterKey.isSelected && (
                        <StyledChip
                          key={filterKey.id}
                          label={filterKey.label}
                          type="input"
                          size="small"
                          buttonIconName="Close"
                          onButtonClick={(e) =>
                            onToggleBox(e, filterKey, 'close-btn')
                          }
                          id={`selected-filter-${filterKey.id}`}
                        />
                      ),
                  )}
                  <Button
                    variant="ghost"
                    size="small"
                    onClick={() => clearFilter('fromSelectedFilterReset')}
                  >
                    {t('Reset filters')}
                  </Button>
                </ChipsWrapper>
              </FiltersWrapper>
            )}
          </ActiveFiltersWrapper>
        </CalDiv>
        {!isLoading && teamsList.length === 0 ? (
          <>
            <EmptyWrapper>
              <EmptyTeamImage />
            </EmptyWrapper>
            <ErrorMessage>
              {t('No results found!')}
              <div>
                {t(
                  'Try using different keywords or adjusting your filters to see if that helps.',
                )}
              </div>
            </ErrorMessage>
          </>
        ) : (
          <StyledTable
            data={teamsList}
            isLoading={isLoading}
            data-testid="wbui-table-teams"
            row={TableRow}
            header={TableHeader}
            rowsPerPage={perPage}
            totalDataCount={totalCount > perPage ? totalCount : undefined}
            rowsPerPageValues={{
              isAll: false,
              pageNumbers: [10, 15, 25, 50],
            }}
            onChange={handleTableChange}
          />
        )}
        {openArchievePopup && (
          <ArchieveModal
            id={arcId}
            isActive={arcActive}
            onClose={closeArchieveDialog}
            callTeam={callTeams}
            text={searchText}
            teamType={archiveTeamType}
          />
        )}
      </div>
      <Notification
        open={alert.show}
        severity={alert.type}
        message={alert.message}
        onClose={handleNotificationClose}
      />
    </>
  );
};
export default TeamsTable;
